/**
 * HH-5 glue: per-list sync stores wrapping the pure queue core
 * (shoplist-queue.js). This module owns every side effect the core
 * deliberately doesn't have — sending ops, the winkelmodus poll timer,
 * online/focus flushing, drop toasts — and it is the only caller of
 * api.lists.addItem/updateItem for the queue's two op kinds (checkoff and
 * add) once a list has a store: both ListDetailScreen and ShoppingScreen
 * read and mutate through the same store, so leaving winkelmodus with
 * unsynced checkoffs can never split-brain into a detail view that shows
 * raw (older) server state.
 *
 * One deliberate exception, so this comment does not lie: setting an
 * item's supermarket category PATCHes api.lists.updateItem directly from
 * ListDetailScreen, as a structural online action alongside
 * delete/rename/pour rather than a queue op. It is safe next to the
 * queue — its follow-up refreshListStore() runs mergeServerItems(), which
 * re-overlays any still-queued local intent — and it touches a field no
 * op kind ever writes.
 *
 * Design decisions, in one place:
 *
 *  - Stores live in a module Map for the whole session (a handful of
 *    lists, trivial memory) and are wiped on logout — a dirty queue must
 *    never survive into another user's session and flush as them.
 *  - One flush loop per store (a shared in-flight promise + the core's
 *    own takeNext refusing while an op is inflight): 'online', 'focus',
 *    'visibilitychange' and the poll interval can all fire together and
 *    still cannot double-send — the WP-2 review's double-flush attack,
 *    answered structurally rather than by timing. Sharing the PROMISE
 *    (not a bare boolean) also gives awaiters real drained-semantics,
 *    which "lijst leegmaken" depends on.
 *  - No standalone retry timer AT ALL: retry is simply the next flush
 *    trigger (a new op, the ~10s winkelmodus tick, online/focus). The
 *    WP-2 review's orphaned-retry-timer bug class is avoided by not
 *    having the timer.
 *  - Polls are single-flight per store, versioned per store (only the
 *    newest refresh may apply — the WP-2 UpcomingPanel stale-response
 *    lesson), and additionally seq-guarded inside the core's merge (a
 *    poll that started before an ack landed is discarded whole).
 *  - Poll failures are silent: freshness polling is not a user action,
 *    and the view it would have refreshed is still the last-known-good
 *    one. Failed FLUSHES set syncError — that's the "niet
 *    gesynchroniseerd" indicator, cleared only when the queue drains.
 *
 * Glue is not unit-tested (it is all I/O and timers); it is exercised
 * end-to-end by the winkelmodus Chromium smoke runs, while every state
 * transition it delegates to lives in the tests-first pure core.
 */
import { signal, effect } from '@preact/signals';
import { api, ApiError } from '../api.js';
import { user, showToast, bumpListsVersion } from '../store.js';
import { __ } from '../i18n.js';
import {
  createState,
  applyCheck,
  applyAdd,
  takeNext,
  ackCheck,
  ackAdd,
  failOp,
  mergeServerItems,
  hasPendingOps,
} from './shoplist-queue.js';

export const POLL_INTERVAL_MS = 10000;

const stores = new Map();
let activePollStore = null;
let listenersAttached = false;

function nowIso() {
  return new Date().toISOString();
}

function newClientKey() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Ancient-WebView fallback; uniqueness only has to hold per list.
  return 'ck-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
}

/**
 * 4xx means the server answered "this op can never succeed" (item deleted
 * elsewhere, list gone) — drop it. Anything else (network, 5xx, rate
 * limiting, an expired session the api layer couldn't self-heal) is a
 * "keep it queued and retry later". Dropping on a dead session would
 * throw away real checkoffs over an auth hiccup.
 */
function isImpossible(err) {
  if (!(err instanceof ApiError) || !err.status) {
    return false;
  }
  if (err.code === 'rest_cookie_invalid_nonce') {
    return false;
  }
  return err.status >= 400 && err.status < 500 && err.status !== 408 && err.status !== 429;
}

function toastDropped(dropped) {
  if (!dropped.length) {
    return;
  }
  if (dropped.length > 1) {
    showToast(__('%d wijzigingen konden niet worden gesynchroniseerd.', 'etm').replace('%d', dropped.length), 'error');
    return;
  }
  const op = dropped[0];
  if (op.kind === 'add') {
    showToast(__('%s kon niet worden toegevoegd.', 'etm').replace('%s', op.label), 'error');
  } else {
    showToast(__('%s is intussen van de lijst verwijderd.', 'etm').replace('%s', op.label), 'info');
  }
}

export function getListStore(listId) {
  ensureGlobalListeners();
  const key = String(listId);
  let store = stores.get(key);
  if (!store) {
    store = {
      listId,
      state: signal(createState([])),
      meta: signal(null), // the list row itself (name, is_template, …)
      loaded: signal(false), // first successful items fetch done
      syncError: signal(false), // a flush failed → "niet gesynchroniseerd"
      // The in-flight drain's promise (null when idle): callers that need
      // "drained" semantics (clearCheckedItems) await the RUNNING drain
      // instead of a no-op early return — review finding G2.
      flushPromise: null,
      // Set by the logout wipe. Clearing the Map only blocks new lookups;
      // an async loop already holding this store must ALSO stop issuing
      // requests, or a queued op could execute under the NEXT user's
      // cookies/nonce on a shared device — review finding G1, the exact
      // invariant this module's header claims.
      dead: false,
      pollInFlight: false,
      lastReqId: 0,
    };
    stores.set(key, store);
  }
  return store;
}

/** After a hard list delete: forget the store, queue included — the rows it tracked no longer exist. */
export function dropListStore(listId) {
  const key = String(listId);
  if (activePollStore && String(activePollStore.listId) === key) {
    activePollStore = null;
  }
  stores.delete(key);
}

export function isDirty(store) {
  return hasPendingOps(store.state.value);
}

/**
 * Fetch the list and seq-guard-merge it into the store. Used by both
 * screens on mount and by the winkelmodus poll. Returns false on any
 * failure or when the response was superseded/stale — callers that need
 * an error state (a first mount with nothing loaded yet) check `loaded`.
 *
 * A seq-discarded response retries exactly once with a fresh seq (review
 * finding G3): the discard means an ack landed mid-flight, and on the
 * detail screen — where mount is the only refresh trigger — silently
 * returning would leave a partial list presented as loaded until the
 * next navigation. meta/loaded are also only committed from an APPLIED
 * response now, never from one the guard ruled possibly-stale.
 */
export async function refreshListStore(store, isRetry = false) {
  const reqId = ++store.lastReqId;
  const seqAtStart = store.state.peek().ackSeq;
  try {
    const res = await api.lists.get(store.listId);
    if (store.dead || reqId !== store.lastReqId) {
      return false; // logged out, or a newer refresh took over mid-flight
    }
    const merged = mergeServerItems(store.state.peek(), res.items || [], seqAtStart);
    if (!merged.applied) {
      return isRetry ? false : refreshListStore(store, true);
    }
    store.state.value = merged.state;
    toastDropped(merged.dropped);
    if (res.list) {
      store.meta.value = res.list;
    }
    store.loaded.value = true;
    return true;
  } catch {
    return false;
  }
}

/** Optimistic checkoff/uncheck: instant local apply, then a flush attempt. */
export function checkItem(store, itemId, checked) {
  store.state.value = applyCheck(store.state.peek(), itemId, checked, nowIso());
  flushStore(store);
}

/** Optimistic add: instant temp row, then a flush attempt. */
export function addItem(store, label) {
  const { state } = applyAdd(store.state.peek(), label, newClientKey(), nowIso());
  store.state.value = state;
  flushStore(store);
}

/**
 * Drain the queue: strictly serial, strictly FIFO. Stops on the first
 * network-class failure (op back to pending, indicator on, next trigger
 * retries); impossible-class failures drop-with-toast and continue with
 * the rest of the queue.
 *
 * Returns the in-flight drain's promise when one is already running
 * (review finding G2): "await flushStore()" then genuinely means "wait
 * until this drain attempt is over", which clearCheckedItems depends on
 * — an early-returning no-op there made "lijst leegmaken" throw a
 * spurious not-synced error on winkelmodus's own happy path whenever
 * the last checkoff's flush was still on the wire.
 *
 * `store.dead` is re-checked after every await (review finding G1): a
 * logout mid-drain must stop the loop before it can issue another
 * request, or a queued op would ride the NEXT session's cookies/nonce.
 */
export function flushStore(store) {
  if (store.flushPromise) {
    return store.flushPromise;
  }
  store.flushPromise = (async () => {
    let ackedAny = false;
    try {
      for (;;) {
        if (store.dead) {
          return;
        }
        const taken = takeNext(store.state.peek());
        if (!taken.op) {
          break;
        }
        store.state.value = taken.state;
        const op = taken.op;
        try {
          if (op.kind === 'add') {
            const res = await api.lists.addItem(store.listId, { label: op.label, client_key: op.clientKey });
            if (store.dead) {
              return;
            }
            const newId = Number(res && res.item_id);
            if (!Number.isFinite(newId) || newId <= 0) {
              // A 200 whose body carries no usable id (a WAF/challenge
              // page the api layer mapped to {}, or the backend's
              // insert-failed edge) must NOT ack — acking would stamp a
              // garbage id on the row and orphan it forever. Treat it as
              // network-class: back to pending, the client_key dedupe
              // makes the retry free (review finding G4).
              const failed = failOp(store.state.peek(), op.opId, { impossible: false });
              store.state.value = failed.state;
              store.syncError.value = true;
              return;
            }
            store.state.value = ackAdd(store.state.peek(), op.opId, newId);
          } else {
            await api.lists.updateItem(store.listId, op.itemId, { checked: op.checked });
            if (store.dead) {
              return;
            }
            store.state.value = ackCheck(store.state.peek(), op.opId);
          }
          ackedAny = true;
        } catch (err) {
          if (store.dead) {
            return;
          }
          const impossible = isImpossible(err);
          const { state, dropped } = failOp(store.state.peek(), op.opId, { impossible });
          store.state.value = state;
          if (!impossible) {
            store.syncError.value = true;
            return;
          }
          toastDropped(dropped);
        }
      }
      if (!hasPendingOps(store.state.peek())) {
        store.syncError.value = false;
        if (ackedAny) {
          bumpListsVersion();
        }
      }
    } finally {
      store.flushPromise = null;
    }
  })();
  return store.flushPromise;
}

/**
 * One winkelmodus heartbeat: a dirty queue flushes (the tick doubles as
 * the retry timer), a clean one polls for the other shopper's changes.
 */
function tick(store) {
  if (hasPendingOps(store.state.peek())) {
    flushStore(store);
  } else {
    pollStore(store);
  }
}

async function pollStore(store) {
  if (store.pollInFlight) {
    return;
  }
  store.pollInFlight = true;
  try {
    await refreshListStore(store);
  } finally {
    store.pollInFlight = false;
  }
}

/**
 * ~10s freshness polling, ONLY while winkelmodus is open (the brief is
 * explicit: started on entry, stopped on leave). Returns the stop
 * function for the screen's unmount cleanup.
 */
export function startShoppingPoll(store) {
  activePollStore = store;
  const id = setInterval(() => tick(store), POLL_INTERVAL_MS);
  return () => {
    clearInterval(id);
    if (activePollStore === store) {
      activePollStore = null;
    }
  };
}

/**
 * "Klaar — lijst leegmaken": drain first (clear-checked only affects rows
 * the server already knows are checked), then the plain online call, then
 * a refresh. Throws to the caller on failure — the screen toasts; there
 * is nothing optimistic about this deliberate end-of-shop action.
 */
export async function clearCheckedItems(store) {
  await flushStore(store);
  if (hasPendingOps(store.state.peek())) {
    throw new ApiError(__('Nog niet alles is gesynchroniseerd. Probeer het zo opnieuw.', 'etm'), 'etm_queue_dirty', 0);
  }
  const res = await api.lists.clearChecked(store.listId);
  await refreshListStore(store);
  bumpListsVersion();
  return res.cleared;
}

function flushAllDirty() {
  for (const store of stores.values()) {
    if (hasPendingOps(store.state.peek())) {
      flushStore(store);
    }
  }
}

function onReturnToApp() {
  flushAllDirty();
  // Winkelmodus open and clean → an immediate freshness poll rather than
  // waiting out the interval (which browsers throttle in hidden tabs).
  if (activePollStore && !hasPendingOps(activePollStore.state.peek())) {
    pollStore(activePollStore);
  }
}

function ensureGlobalListeners() {
  if (listenersAttached || typeof window === 'undefined') {
    return;
  }
  listenersAttached = true;
  window.addEventListener('online', flushAllDirty);
  window.addEventListener('focus', onReturnToApp);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      onReturnToApp();
    }
  });
  // Logout wipes every store: a queue built as one user must never
  // flush as the next (shared-device rule, same reason store.js clears
  // lastCaptureCategoryId). Registered once, here, so store.js needs no
  // import of this module (no cycle). Stores are marked dead BEFORE the
  // Map is cleared — clearing only blocks new lookups, while an async
  // flush/refresh already holding a store reference keeps running; the
  // dead flag is what stops it issuing another request under the next
  // session's cookies (review finding G1).
  effect(() => {
    if (user.value === null) {
      for (const store of stores.values()) {
        store.dead = true;
      }
      stores.clear();
      activePollStore = null;
    }
  });
}
